import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import proxy from "../proxy";
import {
  Box,
  Grid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Text,
  HStack,
  Skeleton,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import useStockPrices from "../hooks/useStockPrices";

// KPI cu randamentul portofoliului: Yield on Cost vs. Current Yield
// Aceeasi formula ca in Stocks.jsx (portfolioYoC / portfolioCurrentYield)
const PortfolioYieldKPI = () => {
  const navigate = useNavigate();
  const [stocks, setStocks] = useState(null);
  const { prices } = useStockPrices();
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    axios
      .get(`${proxy}server_mts/GetStocks.php`, {
        headers: { "Cache-Control": "no-cache" },
      })
      .then((res) => setStocks(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStocks([]));
  }, []);

  const { yieldOnCost, currentYield, annualDividends } = useMemo(() => {
    if (!stocks || stocks.length === 0) {
      return { yieldOnCost: 0, currentYield: 0, annualDividends: 0 };
    }

    const totalInvested = stocks.reduce(
      (sum, s) => sum + s.average_price_share * s.nr_shares,
      0
    );
    const totalValue = stocks.reduce((sum, s) => {
      const price = prices[s.ticker] || s.average_price_share;
      return sum + price * s.nr_shares;
    }, 0);
    const annualDividends = stocks.reduce(
      (sum, s) => sum + s.div_share * s.div_per_year * s.nr_shares,
      0
    );

    return {
      annualDividends,
      yieldOnCost: totalInvested > 0 ? (annualDividends / totalInvested) * 100 : 0,
      currentYield: totalValue > 0 ? (annualDividends / totalValue) * 100 : 0,
    };
  }, [stocks, prices]);

  if (!stocks) {
    return (
      <Box maxW="900px" mx="auto" mt={{ base: 3, md: 4 }} px={{ base: 2, md: 3 }}>
        <Skeleton height="120px" borderRadius="xl" startColor="whiteAlpha.100" endColor="whiteAlpha.200" />
      </Box>
    );
  }

  const color = "#38B2AC";
  const gradient = "linear-gradient(135deg,#38B2AC,#4FD1C5)";

  return (
    <Box maxW="900px" mx="auto" mt={{ base: 3, md: 4 }} px={{ base: 2, md: 3 }}>
      <Box
        position="relative"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => navigate("/stocks")}
        cursor="pointer"
      >
        {/* Glow */}
        <Box
          position="absolute"
          inset="-3px"
          borderRadius="xl"
          bg={gradient}
          opacity={hovered ? 0.3 : 0}
          filter="blur(18px)"
          transition="0.35s"
        />

        <Box
          bg="linear-gradient(135deg, rgba(26,32,44,0.95), rgba(45,55,72,0.95))"
          p={{ base: 3, sm: 4, md: 5 }}
          borderRadius="xl"
          border="2px solid"
          borderColor={hovered ? color : "rgba(56,178,172,0.25)"}
          transition="0.35s"
          transform={hovered ? "translateY(-4px)" : "none"}
          boxShadow={hovered ? `0 20px 40px ${color}44` : "0 10px 25px rgba(0,0,0,0.35)"}
          position="relative"
        >
          <HStack justify="space-between" mb={{ base: 2, md: 3 }}>
            <Text
              color="gray.400"
              fontSize={{ base: "xs", md: "sm" }}
              fontWeight="bold"
              letterSpacing="wider"
            >
              📈{" "}
              <Text as="span" display={{ base: "none", md: "inline" }}>
                Portfolio Yield
              </Text>
              <Text as="span" display={{ base: "inline", md: "none" }}>
                Yield
              </Text>
            </Text>
            <ArrowForwardIcon
              color={color}
              opacity={hovered ? 1 : 0}
              transform={hovered ? "translateX(0)" : "translateX(-5px)"}
              transition="0.25s"
              boxSize={{ base: 3, md: 4 }}
            />
          </HStack>

          <Grid templateColumns="repeat(2, 1fr)" gap={{ base: 2, md: 4 }}>
            {/* Yield on Cost — randamentul vs. ce ai platit */}
            <Stat>
              <StatLabel color="gray.400" fontSize={{ base: "2xs", md: "xs" }} noOfLines={1}>
                Yield on Cost
              </StatLabel>
              <StatNumber
                fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
                fontWeight="black"
                background={gradient}
                backgroundClip="text"
                sx={{ WebkitTextFillColor: "transparent" }}
                textShadow={`0 0 20px ${color}33`}
                lineHeight="1.2"
              >
                {yieldOnCost.toFixed(2)}%
              </StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: "2xs", md: "xs" }} mb={0}>
                vs. avg. buy price
              </StatHelpText>
            </Stat>

            {/* Current Yield — randamentul vs. valoarea de piata */}
            <Stat>
              <StatLabel color="gray.400" fontSize={{ base: "2xs", md: "xs" }} noOfLines={1}>
                Current Yield
              </StatLabel>
              <StatNumber
                fontSize={{ base: "xl", sm: "2xl", md: "3xl" }}
                fontWeight="black"
                color="blue.300"
                lineHeight="1.2"
              >
                {currentYield.toFixed(2)}%
              </StatNumber>
              <StatHelpText color="gray.500" fontSize={{ base: "2xs", md: "xs" }} mb={0}>
                vs. market value
              </StatHelpText>
            </Stat>
          </Grid>

          <HStack
            fontSize={{ base: "2xs", sm: "xs" }}
            color="gray.500"
            mt={{ base: 2, md: 3 }}
            pt={{ base: 2, md: 3 }}
            borderTop="1px solid rgba(255,255,255,0.08)"
            spacing={2}
          >
            <Box boxSize="4px" bg={color} borderRadius="full" flexShrink={0} />
            <Text noOfLines={1}>
              ${annualDividends.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr generated by the portfolio
            </Text>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default PortfolioYieldKPI;
